import { useCallback, useEffect, useMemo } from 'react';
import { type InfiniteData, useQueryClient } from '@tanstack/react-query';
import { echo, useEcho } from '@laravel/echo-react';
import { NotificationType, isNotificationType, shouldToastNotification } from '@/utilities/notification-types';
import { type Notification, type NotificationsResponse } from '@/types/notifications';
import { useAccount } from '@/hooks/data/account/useAccount';
import { useToast } from '@/components/ui/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { QueryKey } from '@/lib/queryKeys';
import { useTranslations } from 'next-intl';

export const useNotificationsChannel = ({
  markNotificationRead,
  handleNotificationClick,
}: {
  markNotificationRead: (params: { notificationId: string; read: boolean }) => Promise<unknown>;
  handleNotificationClick?: (notification: Notification) => Promise<void>;
}) => {
  const queryClient = useQueryClient();

  const { toast } = useToast();
  const tCommon = useTranslations('common');
  const tNotifications = useTranslations('notifications');
  const { profile } = useAccount();
  const userId = profile?.user?.id;

  const channelName = useMemo(() => userId && `App.Models.User.${userId}`, [userId]);
  const eventName = '.Illuminate\\Notifications\\Events\\BroadcastNotificationCreated';

  const handleNotificationDeleted = useCallback(
    ({ notificationIds }: { notificationIds: Array<string> }) => {
      const deletedIds = new Set(notificationIds);

      queryClient.setQueriesData<InfiniteData<NotificationsResponse>>(
        { queryKey: [QueryKey.NotificationList] },
        (current) => {
          if (!current?.pages) {
            return current;
          }
          return {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              notifications: page.notifications.filter((n) => !deletedIds.has(n.id)),
            })),
          };
        },
      );
    },
    [queryClient],
  );

  useEffect(() => {
    if (!channelName) {
      return;
    }

    const channel = echo().private(channelName);
    channel.listen('.notification.deleted', handleNotificationDeleted);
    return () => {
      channel.stopListening('.notification.deleted', handleNotificationDeleted);
    };
  }, [channelName, handleNotificationDeleted]);

  useEcho<Notification>(
    channelName,
    eventName,
    (notification: Notification) => {
      let hasMarkedNotificationAsRead = false;

      const { id: notificationId, cta, readAt } = notification;

      const markToastNotificationAsRead = () => {
        if (hasMarkedNotificationAsRead || readAt || !notificationId) {
          return Promise.resolve();
        }

        hasMarkedNotificationAsRead = true;
        return markNotificationRead({ notificationId, read: true }).catch((error) => {
          hasMarkedNotificationAsRead = false;
          throw error;
        });
      };

      const isArchiveReady = isNotificationType(notification, NotificationType.ArchiveReady);
      const isCommentResolved = isNotificationType(notification, NotificationType.CommentResolved);

      if (shouldToastNotification(notification)) {
        const { dismiss } = toast({
          title: notification.title ?? '',
          description: notification.description ?? '',
          ...((Boolean(cta) || isArchiveReady || isCommentResolved) && {
            action: (
              <ToastAction
                altText={cta?.cta_title ?? (isArchiveReady ? tCommon('download') : tNotifications('viewNotification'))}
                onClick={async () => {
                  if (notification && handleNotificationClick) {
                    await handleNotificationClick(notification);
                    dismiss();
                  }
                }}
              >
                {cta?.cta_title ?? (isArchiveReady ? tCommon('download') : undefined)}
              </ToastAction>
            ),
          }),
          onClick: () => {
            // void handleNotificationClick?.(notification);
            void markToastNotificationAsRead().finally(() => dismiss());
          },
        });
      }

      void queryClient.invalidateQueries({ queryKey: [QueryKey.NotificationList] });
    },
    [channelName, handleNotificationClick, markNotificationRead, queryClient, tCommon, toast, userId],
  );

  return { channelName, userId };
};
